import { useCallback, useEffect, useState } from "react";
import { listSosCases } from "./sosApi.js";

export const SOS_CASES_DEFAULT_PAGE_SIZE = 25;

/**
 * Fetches GET /api/backoffice/sos/cases with real server-side pagination/
 * filtering - mirrors ../abnormal/useAbnormalEventsList.js/
 * ../faceCheck/useFaceCheckDailyList.js. Refetches every time `filters`
 * changes (page, pageSize, search, caseStatus, sosType, severity,
 * superAgentOrganizationId, plcOrganizationId, agentId, employerId,
 * workerId).
 *
 * `filters` must be a stable object reference (the caller should build it
 * with useMemo) - the effect depends on the object directly so it only
 * re-runs when a real filter value actually changes, not on every render.
 *
 * Never fetches when `filters` is falsy (null/undefined) - lets a caller
 * still resolving its own access guard (e.g. an Agent Portal page) pass
 * `null` and get an inert "idle" state instead of an unconditional fetch.
 */
export function useSosCasesList(filters) {
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!filters) {
      setState({ status: "idle", data: null, error: null });
      return undefined;
    }

    let cancelled = false;
    // Previous data (if any) stays visible while a refetch is in flight -
    // this is what lets the page keep the table/KPI strip mounted and show
    // just an inline loading indicator on a filter change, instead of
    // replacing the whole screen with a spinner every keystroke/click.
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));

    listSosCases(filters)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState((previous) => ({ status: "error", data: previous.data, error }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters, reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, refetch };
}
